import { useState } from 'react';
import { useAuth } from './AuthContext';
import { isNativeApp } from './platform';

const S = {
  btn: { background: '#1D9E75', color: '#fff', border: 'none', borderRadius: 8, padding: '11px 20px', fontSize: 14, fontWeight: 500, cursor: 'pointer', width: '100%' },
  btnOutline: { background: '#fff', color: '#333', border: '1px solid #ddd', borderRadius: 8, padding: '11px 20px', fontSize: 14, cursor: 'pointer', width: '100%' },
  btnGoogle: { background: '#fff', color: '#333', border: '1px solid #ddd', borderRadius: 8, padding: '11px 20px', fontSize: 14, cursor: 'pointer', width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10 },
  input: { width: '100%', fontSize: 14, padding: '10px 12px', border: '1px solid #ddd', borderRadius: 8, boxSizing: 'border-box', outline: 'none' },
  label: { fontSize: 13, color: '#666', marginBottom: 6, display: 'block' },
  error: { fontSize: 13, color: '#E53E3E', textAlign: 'center', marginTop: 8 },
  divider: { display: 'flex', alignItems: 'center', gap: 10, margin: '16px 0' },
  dividerLine: { flex: 1, height: 1, background: '#eee' },
  dividerText: { fontSize: 12, color: '#aaa' },
};

export default function AuthScreen() {
  const { loginWithGoogle, loginWithEmail, signupWithEmail, loginAnonymously } = useAuth();
  const [tab, setTab] = useState('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [guestName, setGuestName] = useState('');
  const [showGuestForm, setShowGuestForm] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    setError('');
    setLoading(true);
    try {
      if (tab === 'login') {
        await loginWithEmail(email, password);
      } else {
        if (!name.trim()) { setError('Please enter your name.'); setLoading(false); return; }
        await signupWithEmail(email, password, name.trim());
      }
    } catch (e) {
      if (e.code === 'auth/user-not-found' || e.code === 'auth/wrong-password' || e.code === 'auth/invalid-credential') {
        setError('Incorrect email or password.');
      } else if (e.code === 'auth/email-already-in-use') {
        setError('An account with this email already exists.');
      } else if (e.code === 'auth/weak-password') {
        setError('Password must be at least 6 characters.');
      } else if (e.code === 'auth/invalid-email') {
        setError('Please enter a valid email address.');
      } else {
        setError('Something went wrong. Please try again.');
      }
    }
    setLoading(false);
  };

const handleGoogle = async () => {
  if (isNativeApp) {
    setError('Please use email/password or guest login in the app.');
    return;
  }
  setError('');
  setLoading(true);
  try {
    await loginWithGoogle();
  } catch (e) {
    setError('Google sign-in failed. Please try again.');
  }
  setLoading(false);
};

  const handleGuest = async () => {
    if (!guestName.trim()) { setError('Please enter your first name.'); return; }
    setError('');
    setLoading(true);
    try {
      await loginAnonymously(guestName.trim());
    } catch (e) {
      setError('Could not sign in as guest. Please try again.');
    }
    setLoading(false);
  };

  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', background: '#f5f5f5', fontFamily: 'sans-serif', padding: '1rem' }}>
      <div style={{ background: '#fff', border: '1px solid #e0e0e0', borderRadius: 12, padding: '2rem', maxWidth: 380, width: '100%' }}>

        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <div style={{ fontSize: 32, marginBottom: 4 }}>👑</div>
          <div style={{ fontSize: 24, fontWeight: 500, marginBottom: 4 }}>Four Play</div>
          <div style={{ fontSize: 14, color: '#888' }}>Sign in to save your stats and cosmetics</div>
        </div>

        {!showGuestForm ? (
          <>
            <div style={{ display: 'flex', background: '#f5f5f5', borderRadius: 8, padding: 4, marginBottom: 20 }}>
              {['login', 'signup'].map(t => (
                <button key={t} onClick={() => { setTab(t); setError(''); }} style={{
                  flex: 1, padding: '7px 0', fontSize: 13, border: 'none', borderRadius: 6, cursor: 'pointer',
                  background: tab === t ? '#fff' : 'none',
                  fontWeight: tab === t ? 500 : 400,
                  color: tab === t ? '#000' : '#666',
                }}>{t === 'login' ? 'Sign in' : 'Create account'}</button>
              ))}
            </div>

            {tab === 'signup' && (
              <div style={{ marginBottom: 14 }}>
                <label style={S.label}>Your name</label>
                <input value={name} onChange={e => setName(e.target.value)} placeholder="Enter your name" style={S.input} />
              </div>
            )}

            <div style={{ marginBottom: 14 }}>
              <label style={S.label}>Email address</label>
              <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@example.com" style={S.input} onKeyDown={e => e.key === 'Enter' && handleSubmit()} />
            </div>

            <div style={{ marginBottom: 20 }}>
              <label style={S.label}>Password</label>
              <input type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder={tab === 'signup' ? 'At least 6 characters' : 'Enter your password'} style={S.input} onKeyDown={e => e.key === 'Enter' && handleSubmit()} />
            </div>

            {error && <div style={S.error}>{error}</div>}

            <button onClick={handleSubmit} style={{ ...S.btn, marginTop: error ? 12 : 0 }} disabled={loading}>
              {loading ? '...' : tab === 'login' ? 'Sign in' : 'Create account'}
            </button>

            <div style={S.divider}>
              <div style={S.dividerLine} />
              <span style={S.dividerText}>or</span>
              <div style={S.dividerLine} />
            </div>

            <button onClick={handleGoogle} style={S.btnGoogle} disabled={loading}>
              <svg width="18" height="18" viewBox="0 0 48 48">
                <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
                <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
                <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
                <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
              </svg>
              Continue with Google
            </button>

            <div style={S.divider}>
              <div style={S.dividerLine} />
              <span style={S.dividerText}>or</span>
              <div style={S.dividerLine} />
            </div>

            <button onClick={() => { setShowGuestForm(true); setError(''); }} style={{ ...S.btnOutline, color: '#555', fontSize: 13 }}>
              🎮 Play as guest (5 free games)
            </button>

            <div style={{ fontSize: 11, color: '#aaa', textAlign: 'center', marginTop: 12, lineHeight: 1.5 }}>
              Guest accounts don't save stats or cosmetics.
            </div>
          </>
        ) : (
          <>
            <div style={{ textAlign: 'center', marginBottom: 20 }}>
              <div style={{ fontSize: 15, fontWeight: 500, marginBottom: 4 }}>🎮 Play as guest</div>
              <div style={{ fontSize: 13, color: '#888' }}>No account needed — just enter your first name to start playing!</div>
            </div>

            <div style={{ marginBottom: 8 }}>
              <label style={S.label}>First name</label>
              <input
                value={guestName}
                onChange={e => setGuestName(e.target.value)}
                placeholder="Enter your first name"
                style={S.input}
                onKeyDown={e => e.key === 'Enter' && handleGuest()}
                autoFocus
              />
            </div>

            <div style={{ fontSize: 12, color: '#aaa', marginBottom: 16 }}>
              You get <strong>5 free games</strong> as a guest. Create a free account to keep playing and save your progress!
            </div>

            {error && <div style={S.error}>{error}</div>}

            <button onClick={handleGuest} style={{ ...S.btn, marginBottom: 10 }} disabled={loading}>
              {loading ? '...' : 'Start playing!'}
            </button>

            <button onClick={() => { setShowGuestForm(false); setError(''); }} style={S.btnOutline}>
              Back
            </button>
          </>
        )}
      </div>
    </div>
  );
}
